import React, { useEffect, useState, useCallback } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton, Typography, Box, Checkbox } from '@mui/material';
import renderDateTimePickerField from 'src/shared/components/form/DateTimePicker';
import OrderDetails from './components/OrderDetails';
import OrderPathMapModal from './components/OrderPathMapModal';
import FreeRiderDetails from './components/FreeRiderComponents/FreeRiderDetails';
import OrderDetailsShippingViewDetailsModal from './components/OrderDetailsShippingViewDetailsModal';
import GoodsReceivedItemsModal from './components/GoodsReceivedItemsModal';
import ConfirmationDialog from "src/shared/components/ConfirmationDialog";
import { showNotification, successNofication } from 'src/shared/components/Notification';
import {
  OPERATION_TYPE_FOR_PICKUP_READY,
  OPERATION_TYPE_FOR_GOODS_RECEIVED,
  OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION,
  OPERATION_TYPE_FOR_RE_RESCHEDULE,
  OPERATION_TYPE_FOR_BULK_ORDER_ASSIGNMENT,
  ERROR_MESSAGES,
  PAGE_TIMEOUT
} from '../../../../../utils/pnd/PndConstants';

const OrderDetailsIndex = () => {
  // --- States ---
  const [isRedirect, setRedirect] = useState(false);
  const [reactTableData, setReactTableData] = useState({ tableHeaderData: [], tableRowsData: [] });
  const [loading, setLoading] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [currentOrderType, setCurrentOrderType] = useState(0);
  const [currentPriorityId, setCurrentPriorityId] = useState(0);

  // Modals & Single Selection
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [isAssignOrderPopup, setAssignOrderPopup] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showGoodsModal, setShowGoodsModal] = useState(false);
  const [showBulkGoodsConfirm, setShowBulkGoodsConfirm] = useState(false);
  const [bulkGoodsOrdersList, setBulkGoodsOrdersList] = useState([]);
  const [bulkGoodsSelectedIds, setBulkGoodsSelectedIds] = useState([]);
  const [isGoogleMapView, setGoogleMapView] = useState(null);

  const [newRequestedDate, setNewRequestedDate] = useState("");
  const [newScheduledDate, setNewScheduledDate] = useState("");

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOperationType, setOperationType] = useState(null);
  const [isTitle, setTitle] = useState("");
  const [isOrderId, setOrderId] = useState(null);
  const [isOrderNo, setOrderNo] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  // const userRole = localStorage.getItem("uri");

  // --- Data Fetching ---
  const getAllOrderDetails = useCallback(async (type = currentOrderType, priorityId = currentPriorityId) => {
    setCurrentOrderType(type);
    setCurrentPriorityId(priorityId);
    setLoading(true);
    try {
      const res = await PndRestApi.GetAllOrders({ orderType: type, orderPriorityTypeId: priorityId });
      if (res.status === 200 && res.data.status === 200) {
        setReactTableData({
          tableHeaderData: [], // Header is handled in child or by role columns
          tableRowsData: res.data.data
        });
      } else if (res?.data?.status === 204) {
        setReactTableData({ tableHeaderData: [], tableRowsData: [] });
      } else {
        showNotification("theme-light", "ltr", `Code: ${res.data.status}`, res.data.message);
      }
    } catch (e) {
      showNotification("theme-light", "ltr", "500", ERROR_MESSAGES.tryCatchError);
    } finally { setLoading(false); }
  }, [currentOrderType, currentPriorityId]);

  useEffect(() => { getAllOrderDetails(); }, [getAllOrderDetails]);

  // --- handlers ---
  const edit = (order) => { setSelectedOrder(order); setRedirect(true); };
  const viewDetails = (order) => { setSelectedOrder(order); setViewDetailPopup(true); };
  const openMap = (order) => { setSelectedOrder(order); setGoogleMapView(true); };
  const openMapPickUp = (order) => { setSelectedOrder(order); setGoogleMapView(true); };

  const showDialogBox = (order, type) => {
    setSelectedOrder(order);
    setOperationType(type);
    setOrderId(order.orderId);
    setOrderNo(order.orderNumber);

    if (type === OPERATION_TYPE_FOR_GOODS_RECEIVED) {
      setShowGoodsModal(true);
    } else if (type === OPERATION_TYPE_FOR_RE_RESCHEDULE) {
      setNewRequestedDate("");
      setNewScheduledDate("");
      setShowRescheduleModal(true);
    } else {
      setTitle(type === OPERATION_TYPE_FOR_PICKUP_READY ? "Ready Order" : "Withdraw Order");
      setShowConfirmation(true);
    }
  };

  const OrderRescheduleUpdate = async () => {
    if (!newRequestedDate || !newScheduledDate) {
      showNotification("theme-light", "ltr", "Error", "Please select both a new requested date and a scheduled date.");
      return;
    }
    setShowRescheduleModal(false);
    setLoading(true);
    try {
      const res = await PndRestApi.UpdateOrderStatusDetails({
        operationType: OPERATION_TYPE_FOR_RE_RESCHEDULE,
        orderId: isOrderId,
        hubId: selectedOrder?.hubId,
        requestedDateTimeSlot: new Date(newRequestedDate).toISOString(),
        scheduledDateTimeSlot: new Date(newScheduledDate).toISOString()
      });
      if (res.status === 200 && res.data.status === 200) {
        successNofication("theme-light", "ltr", "Success", "Order Rescheduled Successfully");
        setTimeout(getAllOrderDetails, PAGE_TIMEOUT);
      } else {
        showNotification("theme-light", "ltr", `Error ${res.data.status}`, res.data.message);
      }
    } catch (e) { showNotification("theme-light", "ltr", "500", ERROR_MESSAGES.tryCatchError); }
    finally { setLoading(false); }
  };

  const OrderStatusUpdate = async () => {
    setShowConfirmation(false);
    try {
      const res = await PndRestApi.UpdateOrderStatusDetails({
        operationType: isOperationType,
        orderId: isOrderId,
        hubId: selectedOrder?.hubId
      });
      if (res.status === 200 && res.data.status === 200) {
        successNofication("theme-light", "ltr", "Success", "Status Updated Successfully");
        setTimeout(getAllOrderDetails, PAGE_TIMEOUT);
      } else {
        showNotification("theme-light", "ltr", `Error ${res.data.status}`, res.data.message);
      }
    } catch (e) { showNotification("theme-light", "ltr", "500", ERROR_MESSAGES.tryCatchError); }
  };

  const handleGoodsReceivedConfirm = async (items) => {
    setShowGoodsModal(false);
    try {
      setLoading(true);
      const res = await PndRestApi.UpdateOrderStatusDetails({
        operationType: OPERATION_TYPE_FOR_GOODS_RECEIVED,
        orderId: isOrderId,
        hubId: selectedOrder?.hubId,
        orderItems: items.map(({ _id, isNew, ...rest }) => rest)
      });
      if (res.status === 200 && res.data.status === 200) {
        successNofication("theme-light", "ltr", "Success", "Goods Received Status Updated Successfully");
        setTimeout(getAllOrderDetails, PAGE_TIMEOUT);
      } else {
        showNotification("theme-light", "ltr", `Error ${res.data.status}`, res.data.message);
      }
    } catch (e) {
      showNotification("theme-light", "ltr", "500", ERROR_MESSAGES.tryCatchError);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGoodsReceivedClick = () => {
    const eligibleOrders = selectedOrderIds.map(s => reactTableData.tableRowsData.find(r => r.orderId === s.id)).filter(ord => {
      return ord && ord.orderType === 1 && ord.orderStatusId === OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION;
    });

    if (eligibleOrders.length === 0) {
      showNotification("theme-light", "ltr", "Info", "No eligible Pickup orders selected for Goods Received.");
      return;
    }

    setBulkGoodsOrdersList(eligibleOrders);
    setBulkGoodsSelectedIds(eligibleOrders.map(o => o.orderId));
    setShowBulkGoodsConfirm(true);
  };

  const handleToggleBulkGoodsOrder = (id) => {
    setBulkGoodsSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBulkGoodsReceivedConfirm = async () => {
    setShowBulkGoodsConfirm(false);
    const eligibleOrders = bulkGoodsOrdersList.filter(o => bulkGoodsSelectedIds.includes(o.orderId));

    if (!eligibleOrders.length) {
      showNotification("theme-light", "ltr", "Info", "No orders selected for Goods Received.");
      return;
    }

    setLoading(true);
    try {
      const promises = eligibleOrders.map(async (order) => {
        let items = [];
        try {
          const itemRes = await PndRestApi.GetOrderItemWiseDetails(order.orderId);
          if (itemRes.status === 200 && itemRes.data.status === 200 && Array.isArray(itemRes.data.data) && itemRes.data.data.length > 0) {
            items = itemRes.data.data;
          }
        } catch (err) {
          console.error(`Error fetching items for order ${order.orderNumber}:`, err);
        }

        const processedItems = items.map(({ _id, isNew, ...rest }) => rest);

        return PndRestApi.UpdateOrderStatusDetails({
          operationType: OPERATION_TYPE_FOR_GOODS_RECEIVED,
          orderId: order.orderId,
          hubId: order.hubId,
          orderItems: processedItems
        });
      });

      const results = await Promise.all(promises);
      const successfulCount = results.filter(res => res.status === 200 && res.data.status === 200).length;
      
      if (successfulCount > 0) {
        successNofication("theme-light", "ltr", "Success", `Successfully processed Goods Received for ${successfulCount} order(s).`);
        setSelectedOrderIds([]);
        setTimeout(getAllOrderDetails, PAGE_TIMEOUT);
      } else {
        showNotification("theme-light", "ltr", "Error", "Failed to process bulk Goods Received.");
      }
    } catch (e) {
      showNotification("theme-light", "ltr", "500", ERROR_MESSAGES.tryCatchError);
    } finally {
      setLoading(false);
    }
  };

  const processOrder = (order, type) => {
    setSelectedOrder(order);
    setOperationType(type);
    setOrderId(order.orderId);
    setOrderNo(order.orderNumber);
    setAssignOrderPopup(true);
  };

  // const handleOrderTypeChange = (v) => {
  //   getAllOrderDetails(v);
  //   setSelectedOrderIds([]);
  // };

  const handleBulkAssignClick = () => {
    setOperationType(OPERATION_TYPE_FOR_BULK_ORDER_ASSIGNMENT);
    setAssignOrderPopup(true);
  };

  const handleCheckboxClick = (order) => {
    setSelectedOrderIds(prev => {
      const index = prev.findIndex(item => item.id === order.orderId);
      if (index > -1) return prev.filter(item => item.id !== order.orderId);
      return [...prev, { id: order.orderId, orderNumber: order.orderNumber, orderType: order.orderType, orderStatus: order.orderStatus }];
    });
  };

  if (isRedirect) {
    if (selectedOrder && selectedOrder.orderId) {
      return <Redirect to={`/web_app/pnd_order/order_edit/${selectedOrder.orderId}`} />;
    } else {
      window.location.reload();
    }
  }

  return (
    <div className="order-details-container">
      <OrderDetails
        reactTableData={reactTableData}
        selectedOrderIds={selectedOrderIds}
        setSelectedOrderIds={setSelectedOrderIds}
        handleCheckboxClick={handleCheckboxClick}
        getOrderTypeFilterType={v => getAllOrderDetails(v.value)}
        edit={edit}
        showDialogBox={showDialogBox}
        processOrder={processOrder}
        viewDetails={viewDetails}
        openMap={openMap}
        openMapPickUp={openMapPickUp}
        setAssignOrderPopup={setAssignOrderPopup}
        onBulkAssignClick={handleBulkAssignClick}
        onBulkGoodsReceivedClick={handleBulkGoodsReceivedClick}
        refresh={getAllOrderDetails}
        handleSelectAll={(eligibleOrders) => {
          const allSelected = eligibleOrders.length > 0 && eligibleOrders.every(fo => selectedOrderIds.some(s => s.id === fo.orderId));
          if (allSelected) {
            setSelectedOrderIds(prev => prev.filter(s => !eligibleOrders.some(fo => fo.orderId === s.id)));
          } else {
            setSelectedOrderIds(prev => {
              const newSelections = eligibleOrders
                .filter(fo => !prev.some(s => s.id === fo.orderId))
                .map(o => ({
                  id: o.orderId,
                  orderNumber: o.orderNumber,
                  orderType: o.orderType,
                  orderStatus: o.orderStatus
                }));
              return [...prev, ...newSelections];
            });
          }
        }}
      />

      {loading && <LinearProgress color="primary" sx={{ position: 'fixed', top: 0, left: 0, right: 0 }} />}

      {/* Modals --- Unified Selection Source */}
      {isGoogleMapView && (
        <OrderPathMapModal
          show={isGoogleMapView}
          toggle={() => setGoogleMapView(false)}
          data={selectedOrder}
        />
      )}

      {isAssignOrderPopup && (
        <FreeRiderDetails
          modalShow={isAssignOrderPopup}
          setModalShow={setAssignOrderPopup}
          isOrderNo={isOrderNo}
          isOrderId={isOrderId}
          isOperationType={isOperationType}
          selectedOrderIds={selectedOrderIds}
          selectedDriverId={selectedDriverId}
          setSelectedDriverId={setSelectedDriverId}
          isOrderType={selectedOrder?.orderType || 0}
          data={selectedOrder}
          setRedirect={setRedirect}
          setBulkUploadReDirect={() => getAllOrderDetails()}
        />
      )}

      {viewDetailPopup && (
        <OrderDetailsShippingViewDetailsModal
          modalShow={viewDetailPopup}
          setModalShow={setViewDetailPopup}
          dataInfo={selectedOrder}
        />
      )}

      {showGoodsModal && (
        <GoodsReceivedItemsModal
          open={showGoodsModal}
          onClose={() => setShowGoodsModal(false)}
          orderData={selectedOrder}
          onConfirm={handleGoodsReceivedConfirm}
        />
      )}

      <ConfirmationDialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        title={isTitle || ""}
        message="Are you sure you want to proceed?"
        onConfirm={OrderStatusUpdate}
      />

      <Dialog 
        open={showBulkGoodsConfirm} 
        onClose={() => setShowBulkGoodsConfirm(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #e2e8f0', p: 2 }}>
          Bulk Goods Received Confirm
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Out of the selected orders, please uncheck any orders that you did <b>not</b> receive. Only the checked orders will be marked as Goods Received.
          </Typography>
          <Box sx={{ mt: 2, maxHeight: 300, overflowY: 'auto' }}>
            {bulkGoodsOrdersList.map(order => (
              <Box 
                key={order.orderId} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 1, 
                  borderBottom: '1px solid #f1f5f9',
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                <Checkbox
                  size="small"
                  checked={bulkGoodsSelectedIds.includes(order.orderId)}
                  onChange={() => handleToggleBulkGoodsOrder(order.orderId)}
                />
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Order #{order.orderNumber} {order.erpOrderNo ? `| ERP: ${order.erpOrderNo}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    From: {order.fromName || 'N/A'} | Area: {order.areaName || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MuiButton onClick={() => setShowBulkGoodsConfirm(false)} color="secondary">
            Cancel
          </MuiButton>
          <MuiButton 
            onClick={handleBulkGoodsReceivedConfirm} 
            color="primary" 
            variant="contained"
            disabled={bulkGoodsSelectedIds.length === 0}
          >
            Confirm Goods Received ({bulkGoodsSelectedIds.length})
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog open={showRescheduleModal} onClose={() => setShowRescheduleModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reschedule Order</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Please enter the new requested and scheduled dates for this order.
          </Typography>
          <div style={{ marginTop: '16px', marginBottom: '16px' }}>
            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Requested Time <span className='text-danger'>*</span></label>
            {renderDateTimePickerField({ input: { value: newRequestedDate, onChange: setNewRequestedDate } })}
          </div>
     
          <div style={{ marginTop: '16px', marginBottom: '16px' }}>
            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>New Scheduled Date Time <span className='text-danger'>*</span></label>
            {renderDateTimePickerField({ input: { value: newScheduledDate, onChange: setNewScheduledDate } })}
          </div>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setShowRescheduleModal(false)} color="secondary">
            Cancel
          </MuiButton>
          <MuiButton onClick={OrderRescheduleUpdate} color="primary" variant="contained">
            Confirm Reschedule
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default connect()(OrderDetailsIndex);
